import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import apiService from "../../services/api";
import './ReportsList.css';

const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const closeImage = apiService.getBlobAssetUrl('close_12.svg');
const sendIcon = apiService.getBlobAssetUrl('send_12.svg');
const DEFAULT_COLOR_PALETTE = ['#8884d8', '#2F80ED', '#219653', '#F57921', '#8dd1e1', '#d0ed57'];

const EngagementInsightsDashboard = () => {
  const [insights, setInsights] = useState({});
  const [error, setError] = useState(null);
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [email, setEmail] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showTableDownloadMenu, setShowTableDownloadMenu] = useState(false);
  const dashboardRef = useRef(null);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const data = await apiService.fetchData("surveys/engagementinsights");
        setInsights(data);
      } catch (error) {
        console.error("Error fetching engagement insights:", error);
        setError("Failed to load engagement insights.");
      }
    };

    const loadUserInfo = async () => {
      try {
        const data = await apiService.fetchData('userslist');
        const results = data?.results || [];
        const formatted = results.map((item) => ({
          ID: item.ID || '',
          Name: item.Name || '',
          EmailID: item.EmailID || '',
        }));
        setUsers(formatted);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbarMessage('Failed to load users');
        setSnackbarOpen(true);
      }
    };

    fetchInsights();
    loadUserInfo();
  }, []);

  const monthlyData = (insights.montly_wise_average?.results || []).map(d => ({
    ...d,
    MonthName: `${d.MonthName} ${d.Year}`,
    AverageCSATScore: parseFloat(d.AverageCSATScore),
  }));

  const scoreDistributionData = [
    { ScoreRange: 'Promoters (4.0–5.0)', Percentage: parseFloat(insights.promoterspercentage?.results?.[0]?.Promoters || 0), ResponseCount: Math.round((insights.promoterspercentage?.results?.[0]?.Promoters || 0) * (insights.total_responses?.results?.[0]?.Responses || 1) / 100) },
    { ScoreRange: 'Neutral (3.0–3.9)', Percentage: parseFloat(insights.neutralpercentage?.results?.[0]?.Neutral || 0), ResponseCount: Math.round((insights.neutralpercentage?.results?.[0]?.Neutral || 0) * (insights.total_responses?.results?.[0]?.Responses || 1) / 100) },
    { ScoreRange: 'Detractors (0.0–2.9)', Percentage: parseFloat(insights.detractorspercentage?.results?.[0]?.Detractors || 0), ResponseCount: Math.round((insights.detractorspercentage?.results?.[0]?.Detractors || 0) * (insights.total_responses?.results?.[0]?.Responses || 1) / 100) },
  ];

  const sectionData = (insights.section_wise?.results || [])
    .map((item) => ({
      SectionName: item.SectionName?.trim() || '',
      AvgScore: parseFloat(item.AvgScore),
    }))
    .filter(d => !isNaN(d.AvgScore));

  const subSectionData = (insights.sub_section_wise?.results || []).map(d => ({
    ...d,
    AvgScore: parseFloat(d.AvgScore),
  }));

  const projectData = (insights.project_wise_csat?.results || []).map(d => ({
    ...d,
    AvgCSATScore: parseFloat(d.AvgCSATScore),
    LastSurveyDate: new Date(d.LastSurveyDate).toLocaleDateString(),
  }));

  const projectWiseDistribution = (insights.project_wise?.results || []).map(d => ({
    ...d,
    Promoters: parseInt(d.Promoters),
    Neutral: parseInt(d.Neutral),
    Detractors: parseInt(d.Detractors),
  }));

  const toggleTableDownloadMenu = () => setShowTableDownloadMenu(!showTableDownloadMenu);
  const toggleEmailMenu = () => setShowEmailMenu(!showEmailMenu);

  const hideMenus = () => {
    setShowTableDownloadMenu(false);
    setShowEmailMenu(false);
  };

  const exportDashboardPDF = () => {
    const element = dashboardRef.current;
    if (!element) {
      console.error('Dashboard element not found in ref');
      setSnackbarMessage('Failed to capture dashboard for PDF');
      setSnackbarOpen(true);
      return;
    }

    const clonedElement = element.cloneNode(true);
    clonedElement.style.position = 'absolute';
    clonedElement.style.top = '0';
    clonedElement.style.left = '0';
    clonedElement.style.zIndex = '-1';
    clonedElement.style.background = '#fafafa';
    clonedElement.style.width = '100%';
    clonedElement.style.padding = '20px';
    clonedElement.style.overflow = 'visible';

    // Hide Download and Share buttons
    const downloadButtonTop = clonedElement.querySelector('#settings-download-wrapper2');
    const shareButton = clonedElement.querySelector('#settings-email-wrapper');
    const downloadButtonBottom = clonedElement.querySelector('#settings-download-wrapper1');
    if (downloadButtonTop) downloadButtonTop.style.display = 'none';
    if (shareButton) shareButton.style.display = 'none';
    if (downloadButtonBottom) downloadButtonBottom.style.display = 'none';

    document.body.appendChild(clonedElement);

    html2canvas(clonedElement, {
      useCORS: true,
      scale: 2,
      scrollX: 0,
      scrollY: 0,
      windowWidth: clonedElement.scrollWidth,
      windowHeight: clonedElement.scrollHeight,
    })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const pdfWidth = imgWidth / 96 * 25.4; // Convert pixels to mm
        const pdfHeight = imgHeight / 96 * 25.4;

        const pdf = new jsPDF({
          orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true
        });

        // Add image with scaling to fit
        const ratio = pdf.internal.pageSize.getWidth() / imgWidth;
        const newHeight = imgHeight * ratio;
        pdf.addImage(imgData, 'JPEG', 0, 0, pdf.internal.pageSize.getWidth(), newHeight);

        // Handle pagination if content exceeds page height
        const pageHeight = pdf.internal.pageSize.getHeight();
        if (newHeight > pageHeight) {
          const totalPages = Math.ceil(newHeight / pageHeight);
          for (let i = 1; i < totalPages; i++) {
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, -(pageHeight * i), pdf.internal.pageSize.getWidth(), newHeight);
          }
        }

        pdf.save('engagement_insights_dashboard.pdf');
        document.body.removeChild(clonedElement);
      })
      .catch((error) => {
        console.error('Error generating PDF:', error);
        setSnackbarMessage('Failed to export PDF');
        setSnackbarOpen(true);
        document.body.removeChild(clonedElement);
      });
  };

  const exportTablePDF = () => {
    try {
      const doc = new jsPDF();
      doc.text('Project-wise CSAT Summary', 10, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Project Name', 'Avg CSAT', 'Promoter', 'Neutral', 'Detractor', 'Responses', 'Last Survey']],
        body: projectData.map((row) => [
          row.ProjectName,
          row.AvgCSATScore.toFixed(2),
          `${row.PromoterPercent}%`,
          `${row.NeutralPercent}%`,
          `${row.DetractorPercent}%`,
          row.ResponseCount,
          row.LastSurveyDate,
        ]),
      });
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'project_csat_summary.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table PDF:', error);
      setSnackbarMessage('Failed to export table PDF');
      setSnackbarOpen(true);
    }
    hideMenus();
  };

  const exportTableToCSV = () => {
    try {
      const headers = [
        'Project Name',
        'Avg CSAT',
        'Promoter',
        'Neutral',
        'Detractor',
        'Responses',
        'Last Survey',
      ].join(',');

      const rows = projectData.map((row) =>
        [
          row.ProjectName,
          row.AvgCSATScore.toFixed(2),
          `${row.PromoterPercent}%`,
          `${row.NeutralPercent}%`,
          `${row.DetractorPercent}%`,
          row.ResponseCount,
          row.LastSurveyDate,
        ].join(',')
      );

      const csvContent = `${headers}\n${rows.join('\n')}`;
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'project_csat_summary.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table CSV:', error);
      setSnackbarMessage('Failed to export table CSV');
      setSnackbarOpen(true);
    }
    hideMenus();
  };

  const handleSendDashboardFile = async (fileType) => {
  if (!email) {
    setSnackbarMessage('Please enter an email address.');
    setSnackbarOpen(true);
    return;
  }

  const trimmedEmail = email.trim().toLowerCase();
  const element = dashboardRef.current;

  if (!element) {
    console.error('Dashboard element not found in ref');
    setSnackbarMessage('Failed to capture dashboard for email');
    setSnackbarOpen(true);
    return;
  }

  const emailButton = document.querySelector('#settings-email-wrapper');
  const emailDropdown = document.querySelector('#dashboard-email-dropdown-1');
  const originalEmailButtonStyle = emailButton ? emailButton.style.display : '';
  const originalEmailDropdownStyle = emailDropdown ? emailDropdown.style.display : '';

  if (emailButton) emailButton.style.display = 'none';
  if (emailDropdown) emailDropdown.style.display = 'none';

  const clonedElement = element.cloneNode(true);
  clonedElement.style.position = 'absolute';
  clonedElement.style.top = '0';
  clonedElement.style.left = '0';
  clonedElement.style.zIndex = '-1';
  clonedElement.style.background = '#fafafa';
  clonedElement.style.width = '100%'; // Ensure full width
  clonedElement.style.height = 'auto'; // Allow dynamic height
  clonedElement.style.padding = '20px';
  clonedElement.style.overflow = 'visible'; // Ensure no overflow is hidden

  // Hide Download and Share buttons
  const downloadButtonTop = clonedElement.querySelector('#settings-download-wrapper2');
  const shareButton = clonedElement.querySelector('#settings-email-wrapper');
  const downloadButtonBottom = clonedElement.querySelector('#settings-download-wrapper1');
  if (downloadButtonTop) downloadButtonTop.style.display = 'none';
  if (shareButton) shareButton.style.display = 'none';
  if (downloadButtonBottom) downloadButtonBottom.style.display = 'none';

  // Ensure table container has no overflow constraints
  const tableContainer = clonedElement.querySelector('.resource-csat-table1');
  if (tableContainer) {
    tableContainer.style.overflow = 'visible';
    tableContainer.style.width = '100%'; // Ensure full width
    tableContainer.parentElement.style.overflow = 'visible';
    tableContainer.parentElement.style.width = '100%';
  }

  document.body.appendChild(clonedElement);

  try {
    let attachment;

    if (fileType === 'pdf') {
      const canvas = await html2canvas(clonedElement, {
        useCORS: true,
        scale: 2, // Increase scale for better resolution
        scrollX: 0,
        scrollY: 0,
        windowWidth: clonedElement.scrollWidth, // Capture full width
        windowHeight: clonedElement.scrollHeight, // Capture full height
        logging: true, // Enable logging to debug issues
      });
      if (!canvas) {
        throw new Error('Canvas generation failed');
      }

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pdfWidth = imgWidth / 96 * 25.4; // Convert pixels to mm
      const pdfHeight = imgHeight / 96 * 25.4;

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight], // Dynamic size based on content
        compress: true,
      });

      // Add image with scaling to fit
      const ratio = pdf.internal.pageSize.getWidth() / imgWidth;
      const newHeight = imgHeight * ratio;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdf.internal.pageSize.getWidth(), newHeight);

      // Handle pagination if content exceeds page height
      const pageHeight = pdf.internal.pageSize.getHeight();
      if (newHeight > pageHeight) {
        const totalPages = Math.ceil(newHeight / pageHeight);
        for (let i = 1; i < totalPages; i++) {
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, -(pageHeight * i), pdf.internal.pageSize.getWidth(), newHeight);
        }
      }

      attachment = pdf.output('datauristring');
    } else {
      // CSV logic remains unchanged
      const headers = [
        'Metric', 'Value',
        'Month', 'Average CSAT Score',
        'Score Range', 'Percentage',
        'Section Name', 'Average Score',
        'Sub Section Name', 'Average Score',
        'Project Name', 'Avg CSAT', 'Promoter', 'Neutral', 'Detractor', 'Responses', 'Last Survey',
      ].join(',');

      const metricsRows = [
        `Overall CSAT,${parseFloat(insights.average_rating?.results?.[0]?.AverageRating || 0).toFixed(2)}`,
        `% Promoters,${insights.promoterspercentage?.results?.[0]?.Promoters || 0}%`,
        `% Neutral,${insights.neutralpercentage?.results?.[0]?.Neutral || 0}%`,
        `% Detractors,${insights.detractorspercentage?.results?.[0]?.Detractors || 0}%`,
        `Response Count,${insights.total_responses?.results?.[0]?.Responses || 0}`,
      ].join('\n');

      const monthlyRows = monthlyData.map(row =>
        ['', '', row.MonthName, row.AverageCSATScore.toFixed(2)].join(',')
      ).join('\n');

      const scoreRows = scoreDistributionData.map(row =>
        ['', '', '', '', row.ScoreRange, row.Percentage.toFixed(2)].join(',')
      ).join('\n');

      const sectionRows = sectionData.map(row =>
        ['', '', '', '', '', '', row.SectionName, row.AvgScore.toFixed(2)].join(',')
      ).join('\n');

      const subSectionRows = subSectionData.map(row =>
        ['', '', '', '', '', '', '', '', row.SubSectionName, row.AvgScore.toFixed(2)].join(',')
      ).join('\n');

      const projectRows = projectData.map(row =>
        [
          '', '', '', '', '', '', '', '', '', '',
          row.ProjectName,
          row.AvgCSATScore.toFixed(2),
          `${row.PromoterPercent}%`,
          `${row.NeutralPercent}%`,
          `${row.DetractorPercent}%`,
          row.ResponseCount,
          row.LastSurveyDate,
        ].join(',')
      ).join('\n');

      const csvContent = `${headers}\n${metricsRows}\n${monthlyRows}\n${scoreRows}\n${sectionRows}\n${subSectionRows}\n${projectRows}`;
      attachment = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    }

    const emailData = {
      recipientName: trimmedEmail.split('@')[0] || 'Recipient',
      organizationName: 'CriticalRiver Technologies',
      dashboardTitle: 'Engagement Level Dashboard Report',
      to: trimmedEmail,
      subject: `Engagement Level Dashboard Report (${fileType.toUpperCase()})`,
      attachment: attachment,
    };

    const response = await apiService.sendDashboardEmail(
      emailData.recipientName,
      null,
      null,
      emailData.organizationName,
      emailData.dashboardTitle,
      emailData.to,
      emailData.subject,
      null,
      emailData.attachment
    );

    setSnackbarMessage(response.message || `Report sent successfully to ${trimmedEmail}!`);
    setSnackbarOpen(true);
    setEmail('');
    setShowEmailMenu(false);
  } catch (error) {
    console.error('Error sending dashboard email:', error);
    console.error('Full error details:', {
      message: error.message,
      response: error.response ? error.response.data : 'No response',
      status: error.response ? error.response.status : 'Unknown',
    });
    if (error.message.includes('554') || error.message.includes('MaxSubmissionExceeded')) {
      setSnackbarMessage('Email failed: Attachment size exceeds server limit. Please download the file manually.');
    } else {
      setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to send dashboard email');
    }
    setSnackbarOpen(true);
  } finally {
    if (emailButton) emailButton.style.display = originalEmailButtonStyle;
    if (emailDropdown) emailDropdown.style.display = originalEmailDropdownStyle;

    if (clonedElement.parentNode === document.body) {
      document.body.removeChild(clonedElement);
    }
  }
};

  const handleClearInput = () => {
    setEmail('');
    setSuggestions([]);
  };

  const handleEmailInputChange = (e) => {
    const input = e.target.value;
    setEmail(input);

    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }

    const filteredSuggestions = users.filter(
      (user) =>
        user?.EmailID?.toLowerCase().includes(input.toLowerCase()) ||
        user?.Name?.toLowerCase().includes(input.toLowerCase())
    );

    setSuggestions(filteredSuggestions);
  };

  const handleSuggestionClick = (user) => {
    setEmail(user.EmailID);
    setSuggestions([]);
  };

  const renderSuggestionText = (text, searchTerm) => {
    if (!searchTerm) return <span>{text}</span>;

    const lowerText = text?.toLowerCase() ?? '';
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);

    if (index === -1) return <span>{text}</span>;

    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + searchTerm.length);
    const afterMatch = text.substring(index + searchTerm.length);

    return (
      <>
        <span className="suggestion-non-match">{beforeMatch}</span>
        <span className="suggestion-match">{match}</span>
        <span className="suggestion-non-match">{afterMatch}</span>
      </>
    );
  };

  return (
    <section>
      <div className="dashboard" ref={dashboardRef}>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <div className="resource-dashboard-actions-right" style={{ position: 'relative', marginBottom: '15px' }}>
          <button
            className="icon-btn"
            id="settings-download-wrapper2"
            onClick={exportDashboardPDF}
            aria-label="Download entire dashboard as PDF"
          >
            <img src={downloadIcon} alt="Download Entire Dashboard" className="action-icon" />
            <span>Download</span>
          </button>
          <button
            className="icon-btn"
            id="settings-email-wrapper"
            onClick={toggleEmailMenu}
            aria-label="Toggle email options for entire dashboard"
          >
            <img src={sendIcon} alt="Send" className="action-icon" />
            <span>Share</span>
          </button>
          {showEmailMenu && (
            <div className="dashboard-email-dropdown" id="dashboard-email-dropdown-1">
              <div className="email-input-wrapper">
                <input
                  type="email"
                  value={email}
                  aria-label="Enter email address"
                  onChange={handleEmailInputChange}
                  placeholder="Enter email address"
                  style={{ padding: '5px', marginBottom: '5px', width: '100%', minWidth: '180px' }}
                />
                {email && (
                  <button
                    type="button"
                    className="clear-img11"
                    id="clear-img11-1"
                    onClick={handleClearInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleClearInput();
                      }
                    }}
                    aria-label="Clear email input"
                    tabIndex={0}
                  >
                    <img src={closeImage} alt="clear" />
                  </button>
                )}
                {suggestions.length > 0 && (
                  <ul className="suggestions-dropdown" id="suggestions-dropdown-1">
                    {suggestions.map((user, index) => (
                      <li
                        key={user.ID}
                        onClick={() => handleSuggestionClick(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSuggestionClick(user);
                          }
                        }}
                        className={`suggestion-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                        tabIndex={0}
                      >
                        <div>{renderSuggestionText(user.Name, email)}</div>
                        <div>{renderSuggestionText(user.EmailID, email)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="dashboard-export-option" onClick={() => handleSendDashboardFile('pdf')}>
                Send PDF
              </div>
            </div>
          )}
        </div>
        <div className="ams-dashboard-wrapper">
          <div className="ams-dashboard">
          <div className="dashboard-section">
              <div className="cards-container">
                {[
                  { label: 'Overall CSAT', value: parseFloat(insights.average_rating?.results?.[0]?.AverageRating || 0).toFixed(2), className: 'orange' },
                  { label: '% Promoters', value: `${insights.promoterspercentage?.results?.[0]?.Promoters || 0} %`, className: 'green' },
                  { label: '% Neutral', value: `${insights.neutralpercentage?.results?.[0]?.Neutral || 0} %`, className: 'blue' },
                  { label: '% Detractors', value: `${insights.detractorspercentage?.results?.[0]?.Detractors || 0} %`, className: 'darkblue' },
                  { label: 'Response Count', value: insights.total_responses?.results?.[0]?.Responses || 0, className: 'purple' },
                ].map((card) => (
                  <div key={card.label} className={`ams-card ${card.className}`} style={{ boxShadow: 'none', border: 'none' }}>
                    <span className="ams-card-title">{card.label}</span>
                    <span className="ams-card-value">{card.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">CSAT Score Trend</h3>
                    {monthlyData.length ? (
                      <ResponsiveContainer id="chart9" width="100%" height={250}>
                        <LineChart
                          data={monthlyData} 
                          margin={{ top: 1, right: 20, left: 30, bottom: 35 }}
                        >
                          <XAxis
                            dataKey="MonthName"
                            stroke="#666"
                            interval={0}
                            tick={{ fontSize: 11, angle: -35, textAnchor: 'end' }}
                          />
                          <YAxis
                            domain={[0, 5]}
                            stroke="#666"
                            ticks={[0, 1, 2, 3, 4, 5]}
                            label={{
                              value: "Average Score",
                              angle: -90,
                              position: "insideLeft",
                              offset: -5,
                              style: { textAnchor: "middle", fill: "#666", fontSize: 12 },
                            }}
                          />
                          <Tooltip />
                          <Legend verticalAlign="top" height={30} />
                          <Line
                            type="monotone"
                            dataKey="AverageCSATScore"
                            stroke="#FF7300"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>

                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Score Distribution</h3>
                    {scoreDistributionData.length ? (
                      <ResponsiveContainer id="chart8" width="100%" height={220}>
                        <BarChart layout="vertical" data={scoreDistributionData} margin={{ top: 15, right: 0, left: 5, bottom: -20 }}>
                          <XAxis type="number" domain={[0, Math.max(...scoreDistributionData.map(d => d.ResponseCount)) * 1]} stroke="#666" />
                          <YAxis dataKey="ScoreRange" type="category" width={150} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="ResponseCount" fill="#3498db" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Average Rating per Section</h3>
                    {scoreDistributionData.length ? (
                      <ResponsiveContainer id="chart9" width="100%" height={220} style={{ marginLeft: '10px', fontSize: 12 }}>
                        <PieChart>
                          <Pie
                            style={{ fontSize: 12 }}
                            data={sectionData}
                            dataKey="AvgScore"
                            nameKey="SectionName"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            labelLine={false}
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {sectionData.map((entry, index) => (
                              <Cell
                                style={{ fontSize: 12 }}
                                key={`cell-${index}`}
                                fill={DEFAULT_COLOR_PALETTE[index % DEFAULT_COLOR_PALETTE.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>

                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Average Rating per Sub-Section</h3>
                    {sectionData.length ? (
                      <ResponsiveContainer id="chart91" width="100%" height={260}>
                        <BarChart layout="vertical" data={subSectionData}>
                          <XAxis
                            type="number"
                            domain={[0, 4]}
                            stroke="#666"
                            label={{ value: "", angle: 0, position: "bottom" }}
                          />
                          <YAxis
                            dataKey="SubSectionName"
                            type="category"
                            width={150}
                            interval={0}
                            tick={{ fontSize: 12, textAnchor: "end" }}
                          />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="AvgScore" name="Average CSAT Score" fill="#3498db" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>
              </div>

              <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff', width: '100%', height: 420, marginTop: 20 }}>
                <div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-3" id="chart1">Project-wise Distribution</h3>
                  {projectWiseDistribution.length ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={projectWiseDistribution} 
                        margin={{ top: -10, right: 30, left: 0, bottom: 80 }}
                        barCategoryGap={12}
                        barGap={2}
                      >
                        <XAxis
                          dataKey="Name"
                          tick={{ fontSize: 12, angle: -40, textAnchor: "end" }}
                          interval={0}
                          height={80}
                        />
                        <XAxis
                          xAxisId="index"
                          dataKey="index"
                          type="number"
                          domain={[0, projectWiseDistribution.length]}
                          hide
                        />
                        <YAxis
                          allowDecimals={false}
                          label={{ value: "Count", angle: -90, position: "insideLeft" }}
                        />
                        <Tooltip />
                        <Legend verticalAlign="top" align="center" height={36} wrapperStyle={{ marginBottom: 10 }} />
                        <Bar dataKey="Promoters" fill="#2ecc71" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        <Bar dataKey="Neutral" fill="#3498db" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        <Bar dataKey="Detractors" fill="#FF7300" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        {projectWiseDistribution.map((_, i) =>
                          i === projectWiseDistribution.length - 1 ? null : (
                            <ReferenceLine
                              key={`divider-${i}`}
                              xAxisId="index"
                              x={i + 0.98}
                              stroke="#ccc"
                              strokeDasharray="3 3"
                            />
                          )
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p>No data available</p>
                  )}
                </div>
              </div><br/>

              <div style={{ boxShadow: 'none', border: 'none', padding: 0, width: '100%' }}>
                <div id="results4">
                  <h3 className="text-lg font-semibold text-gray-600 mb-3" id="chart1">Project-wise CSAT Score</h3>
                  <div className="settings-download-wrapper" id="settings-download-wrapper1">
                    <button
                      className="icon-btn"
                      onClick={toggleTableDownloadMenu}
                      aria-label="Toggle download options"
                    >
                      <img src={downloadIcon} alt="Download" className="action-icon"/>
                      <span>Download</span>
                    </button>
                    {showTableDownloadMenu && (
                      <div className="Project-export-dropdown">
                        <button
                          id="Project-export-option11"
                          className="Project-export-option"
                          onClick={() => {
                            exportTableToCSV();
                            toggleTableDownloadMenu();
                            hideMenus();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              exportTableToCSV();
                              toggleTableDownloadMenu();
                              hideMenus();
                            }
                          }}
                        >
                          Download CSV
                        </button>
                        <button
                          id="Project-export-option11"
                          className="Project-export-option"
                          onClick={() => {
                            exportTablePDF();
                            toggleTableDownloadMenu();
                            hideMenus();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              exportTablePDF();
                              toggleTableDownloadMenu();
                              hideMenus();
                            }
                          }}
                        >
                          Download PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {projectData.length ? (
                  <TableContainer component={Paper} style={{ boxShadow: 'none',textAlign:'center' }}>
                    <Table className="resource-csat-table1">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Project Name</strong></TableCell>
                          <TableCell><strong>Avg CSAT</strong></TableCell>
                          <TableCell><strong>Promoter</strong></TableCell>
                          <TableCell><strong>Neutral</strong></TableCell>
                          <TableCell><strong>Detractor</strong></TableCell>
                          <TableCell><strong>Responses</strong></TableCell>
                          <TableCell><strong>Last Survey</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectData.map((project) => (
                          <TableRow key={project.ProjectName}>
                            <TableCell>{project.ProjectName}</TableCell>
                            <TableCell>{project.AvgCSATScore.toFixed(2)}</TableCell>
                            <TableCell>{project.PromoterPercent}%</TableCell>
                            <TableCell>{project.NeutralPercent}%</TableCell>
                            <TableCell>{project.DetractorPercent}%</TableCell>
                            <TableCell>{project.ResponseCount}</TableCell>
                            <TableCell>{project.LastSurveyDate}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <p>No data available</p>}
              </div>
            </div>
          </div>
        </div>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{ '& .MuiSnackbarContent-root': { backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#f44336' } }}
        />
      </div>
    </section>
  );
};

const getScoreRange = (score) => {
  if (score >= 4.0) return 'Promoters (4.0–5.0)';
  if (score >= 3.0) return 'Neutral (3.0–3.9)';
  return 'Detractors (0.0–2.9)';
};

export default EngagementInsightsDashboard;